import { useEffect, useState } from "react";
import AccountOptionsTable from "../../Tables/ApplyOption/PortfolioBuyingPosition";
import {
  IApplyOptionsCommonSearchParam,
  OrderSide,
} from "../../Interfaces/applyOption";
import LastTradingBookTable from "../../Tables/ApplyOption/LastTradingBookTable";
import SubmitRequestsTable from "../../Tables/ApplyOption/SubmitRequestsTable";
import ConflictTable from "../../Tables/ApplyOption/ConflictTable/ConflictTable";
import OmsResultTable from "../../Tables/ApplyOption/OmsResultTable/OmsResultTable";
import UsersDropdown from "../../Components/Dropdown/Users/UsersDropdown";
import TabBox, { Tab } from "../../Components/Tabbox/TabBox";
import ErrorsTable from "../../Tables/Order/ErrorsTable/ErrorsTable";
import {
  getAccountOptionExcelFile,
  getApplyOptionBrokers,
  getApplyOptionUsers,
  getApplyOptionConfig,
  getApplyOptionPlanStatusDropdown,
  getApplyPlanTableExcelFile,
  getConflictTableExcelFile,
  getErrorTable,
  getErrorTableExcelFile,
  getLastTradingBookExcelFile,
  getOmsResultTableExcelFile,
  getSubmitRequestsExcelFile,
} from "../../Requests/applyOption";
import FileDownloader from "../../Components/FileDownloader";
import FailedUsers from "./FailedUsers";
import ApplyPlanTable from "src/Tables/ApplyOption/ApplyPlan/ApplyPlanTable";
import { CheckBox, DynamicDropdown, StaticDropdown } from "src/Components";
import { getStepInstruments } from "src/Requests/stepRequests";
import { useErrorTabCount } from "src/Hooks/useErrorTabCount";

enum ActiveTab {
  AccountOption,
  FailedUsers,
  TradingBook, // قدرت خرید مشتقه
  ApplyPlan, // برنامه اعمال
  SubmitRequests, // اعمال ثبت شده
  Conflict, // مغایرت
  OmsResult, // نتایج اعمال
  Error,
}

interface IProps {
  activeTableRefreshFlag: boolean;
  configVersion: number;
}


const brokerFilterTabs = [
  ActiveTab.TradingBook,
  ActiveTab.ApplyPlan,
  ActiveTab.SubmitRequests,
  ActiveTab.Conflict,
  ActiveTab.OmsResult,
  ActiveTab.Error,
];

const positionSideOptions = [
  { id: OrderSide.Buy, name: "خرید" },
  { id: OrderSide.Sell, name: "فروش" },
];

const TablesWrapper = ({ activeTableRefreshFlag, configVersion }: IProps) => {
  const [activeTab, setActiveTab] = useState(ActiveTab.AccountOption);
  const [isApplyDay, setIsApplyDay] = useState(false);
  const [searchParams, setSearchParams] = useState(
    {} as IApplyOptionsCommonSearchParam,
  );
  const [brokerOptions, setBrokerOptions] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    getApplyOptionBrokers().then((brokers) => setBrokerOptions(brokers));
  }, []);

  useEffect(() => {
    getApplyOptionConfig()
      .then((config) => setIsApplyDay(config.applyRemainedDay === 0))
      .catch(() => setIsApplyDay(false));
  }, [activeTab, configVersion]);

  const { errorTabCount, errorTableRefreshFlag, errorTableAfterFetchHandler } =
    useErrorTabCount("applyOption", activeTab, ActiveTab.Error, getErrorTable);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      insId: undefined,
      status: undefined,
      showInactive: activeTab === ActiveTab.ApplyPlan ? true : undefined,
      showOldPortfolio: undefined,
      showConflictsOnly: undefined,
      showOutOfTolerance: undefined,
      onlyShowPlansWithResult: undefined,
      positionSide: undefined,
    });
  }, [activeTab]);

  const getTable = () => {
    switch (activeTab) {
      case ActiveTab.AccountOption:
        return (
          <AccountOptionsTable
            searchParams={searchParams}
            refreshFlag={activeTableRefreshFlag}
          />
        );

      case ActiveTab.TradingBook:
        return (
          <LastTradingBookTable
            searchParams={searchParams}
            refreshFlag={activeTableRefreshFlag}
          />
        );

      case ActiveTab.ApplyPlan:
        return (
          <ApplyPlanTable
            refreshFlag={activeTableRefreshFlag}
            searchParams={searchParams}
          />
        );

      case ActiveTab.SubmitRequests:
        return (
          <SubmitRequestsTable
            searchParams={searchParams}
            refreshFlag={activeTableRefreshFlag}
          />
        );

      case ActiveTab.Conflict:
        return (
          <ConflictTable
            searchParams={searchParams}
            refreshFlag={activeTableRefreshFlag}
          />
        );

      case ActiveTab.OmsResult:
        return (
          <OmsResultTable
            searchParams={searchParams}
            refreshFlag={activeTableRefreshFlag}
          />
        );

      case ActiveTab.Error:
        return (
          <ErrorsTable
            searchParams={searchParams}
            getData={getErrorTable}
            onFetchComplete={errorTableAfterFetchHandler}
            refreshFlag={errorTableRefreshFlag || activeTableRefreshFlag}
          />
        );

      case ActiveTab.FailedUsers:
        return <FailedUsers refreshFlag={activeTableRefreshFlag} />;

      default:
        return <></>;
    }
  };

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParams({ ...searchParams, [name]: value });
  };

  const tabChildren: Tab[] = [
    { index: ActiveTab.FailedUsers, title: "اتصال اکانت" },
    { index: ActiveTab.AccountOption, title: "موقعیت خرید" },
    { index: ActiveTab.TradingBook, title: "قدرت خرید مشتقه" },
    { index: ActiveTab.ApplyPlan, title: "برنامه اعمال" },
    { index: ActiveTab.SubmitRequests, title: "اعمال ثبت شده" },
    { index: ActiveTab.Conflict, title: "مغایرت" },
    { index: ActiveTab.OmsResult, title: "نتایج اعمال" },
    { index: ActiveTab.Error, title: "خطا", count: errorTabCount },
  ];

  const getExcelFileHandler = () => {
    switch (activeTab) {
      case ActiveTab.AccountOption:
        return getAccountOptionExcelFile(searchParams);

      case ActiveTab.SubmitRequests:
        return getSubmitRequestsExcelFile(searchParams);

      case ActiveTab.TradingBook:
        return getLastTradingBookExcelFile(searchParams);

      case ActiveTab.ApplyPlan:
        return getApplyPlanTableExcelFile(searchParams);

      case ActiveTab.Error:
        return getErrorTableExcelFile(searchParams);

      case ActiveTab.Conflict:
        return getConflictTableExcelFile(searchParams);

      case ActiveTab.OmsResult:
        return getOmsResultTableExcelFile(searchParams);

      default:
        break;
    }
  };

  return (
    <div className="w-full py-3 px-1 border border-gray-300 rounded flex flex-col justify-start items-start gap-5">
      <div className="flex flex-row justify-start items-center gap-3">
        <div className="w-min">
          <TabBox
            activeTab={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            children={tabChildren}
          />
        </div>

        {activeTab !== ActiveTab.FailedUsers ? (
          <FileDownloader getFile={getExcelFileHandler as any} />
        ) : (
          <></>
        )}
      </div>
      <div className="flex flex-row justify-start items-center gap-1">
        <div className="w-44">
          <UsersDropdown
            label="نام اکانت"
            getData={getApplyOptionUsers}
            value={searchParams.accountId}
            name="accountId"
            onChange={searchParamChangeHandler}
          />
        </div>
        {brokerFilterTabs.includes(activeTab) && (
          <div className="w-44">
            <StaticDropdown
              dropdown={{ name: "broker", placeholder: "کارگزاری" }}
              options={brokerOptions}
              value={searchParams.broker}
              onChange={searchParamChangeHandler}
            />
          </div>
        )}
        {isApplyDay &&
          (activeTab === ActiveTab.AccountOption || activeTab === ActiveTab.TradingBook) && (
            <CheckBox
              checked={searchParams.showOldPortfolio !== false}
              label="نمایش سطرهای پورتفوی قبلی"
              name="showOldPortfolio"
              onChange={searchParamChangeHandler}
            />
          )}
        {activeTab === ActiveTab.ApplyPlan ? (
          <>
            <div className="w-44">
              <DynamicDropdown
                dropdown={{
                  placeholder: "نماد",
                  name: "insId",
                }}
                getData={getStepInstruments}
                value={searchParams.insId}
                onChange={searchParamChangeHandler}
              />
            </div>
            <div className="w-44">
              <DynamicDropdown
                dropdown={{
                  placeholder: "وضعیت",
                  name: "status",
                }}
                getData={getApplyOptionPlanStatusDropdown}
                value={searchParams.status}
                onChange={searchParamChangeHandler}
              />
            </div>
            <CheckBox
              checked={searchParams?.showInactive ?? true}
              label="نمایش غیر فعال ها"
              name="showInactive"
              onChange={searchParamChangeHandler}
            />
          </>
        ) : activeTab === ActiveTab.Conflict ? (
          <>
            <CheckBox
              checked={searchParams?.showConflictsOnly || false}
              label="فقط مغایرت دار"
              name="showConflictsOnly"
              onChange={searchParamChangeHandler}
            />
            <CheckBox
              checked={searchParams?.showOutOfTolerance || false}
              label="نمایش خارج از حاشیه"
              name="showOutOfTolerance"
              onChange={searchParamChangeHandler}
            />
          </>
        ) : activeTab === ActiveTab.OmsResult ? (
          <>
            <div className="w-44">
              <StaticDropdown
                dropdown={{ name: "positionSide", placeholder: "موقعیت" }}
                options={positionSideOptions}
                value={searchParams.positionSide}
                onChange={searchParamChangeHandler}
              />
            </div>
            <CheckBox
              checked={searchParams?.onlyShowPlansWithResult || false}
              label="فقط دارای نتیجه"
              name="onlyShowPlansWithResult"
              onChange={searchParamChangeHandler}
            />
          </>
        ) : (
          <></>
        )}
      </div>

      {getTable()}
    </div>
  );
};

export default TablesWrapper;
